'use client';
import { useEffect, useRef, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';

export type OrderStatus =
  | 'pending' |'searching_for_rider' |'rider_assigned' |'on_delivery' |'delivered' |'cancelled';

export interface RealtimeOrder {
  id: string;
  order_number: string;
  customer_id: string;
  rider_id: string | null;
  pickup_address: string;
  delivery_address: string;
  recipient_name: string | null;
  package_type: string;
  is_fragile: boolean;
  distance_km: number;
  total_amount: number;
  payment_method: string;
  status: OrderStatus;
  created_at: string;
  updated_at: string;
}

export interface RealtimeRiderRequest {
  id: string;
  order_id: string;
  rider_id: string;
  status: 'pending' | 'accepted' | 'rejected' | 'expired';
  expires_at: string;
  created_at: string;
}

interface UseRealtimeOrdersOptions {
  /** Filter orders by customer_id (Customer screen) */
  customerId?: string;
  /** Filter rider_requests by rider_id (Rider screen) */
  riderId?: string;
  /** Subscribe to ALL orders (Admin screen) */
  adminMode?: boolean;
  onOrderChange?: (payload: {
    eventType: 'INSERT' | 'UPDATE' | 'DELETE';
    newRecord?: RealtimeOrder;
    oldRecord?: Partial<RealtimeOrder>;
  }) => void;
  onRiderRequestChange?: (payload: {
    eventType: 'INSERT' | 'UPDATE' | 'DELETE';
    newRecord?: RealtimeRiderRequest;
    oldRecord?: Partial<RealtimeRiderRequest>;
  }) => void;
}

export function useRealtimeOrders({
  customerId,
  riderId,
  adminMode = false,
  onOrderChange,
  onRiderRequestChange,
}: UseRealtimeOrdersOptions) {
  const channelRef = useRef<RealtimeChannel | null>(null);
  const riderChannelRef = useRef<RealtimeChannel | null>(null);

  const onOrderChangeRef = useRef(onOrderChange);
  const onRiderRequestChangeRef = useRef(onRiderRequestChange);

  useEffect(() => {
    onOrderChangeRef.current = onOrderChange;
  }, [onOrderChange]);

  useEffect(() => {
    onRiderRequestChangeRef.current = onRiderRequestChange;
  }, [onRiderRequestChange]);

  const subscribe = useCallback(() => {
    const supabase = createClient();

    // --- Orders channel ---
    if (onOrderChangeRef.current) {
      let orderFilter: string | undefined;

      if (customerId) {
        orderFilter = `customer_id=eq.${customerId}`;
      } else if (riderId) {
        orderFilter = `rider_id=eq.${riderId}`;
      }
      // adminMode: no filter — subscribe to all

      const channelName = adminMode
        ? 'admin_orders_realtime'
        : customerId
        ? `customer_orders_${customerId}`
        : riderId
        ? `rider_orders_${riderId}`
        : 'orders_realtime';

      channelRef.current = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'orders',
            ...(orderFilter ? { filter: orderFilter } : {}),
          },
          (payload) => {
            onOrderChangeRef.current?.({
              eventType: payload.eventType as 'INSERT' | 'UPDATE' | 'DELETE',
              newRecord: payload.new as RealtimeOrder,
              oldRecord: payload.old as Partial<RealtimeOrder>,
            });
          }
        )
        .subscribe();
    }

    // --- Rider requests channel (for rider screen) ---
    if (riderId && onRiderRequestChangeRef.current) {
      riderChannelRef.current = supabase
        .channel(`rider_requests_${riderId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'rider_requests',
            filter: `rider_id=eq.${riderId}`,
          },
          (payload) => {
            onRiderRequestChangeRef.current?.({
              eventType: payload.eventType as 'INSERT' | 'UPDATE' | 'DELETE',
              newRecord: payload.new as RealtimeRiderRequest,
              oldRecord: payload.old as Partial<RealtimeRiderRequest>,
            });
          }
        )
        .subscribe();
    }

    // --- Admin: also subscribe to rider_requests ---
    if (adminMode && onRiderRequestChangeRef.current) {
      riderChannelRef.current = supabase
        .channel('admin_rider_requests_realtime')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'rider_requests',
          },
          (payload) => {
            onRiderRequestChangeRef.current?.({
              eventType: payload.eventType as 'INSERT' | 'UPDATE' | 'DELETE',
              newRecord: payload.new as RealtimeRiderRequest,
              oldRecord: payload.old as Partial<RealtimeRiderRequest>,
            });
          }
        )
        .subscribe();
    }
  }, [customerId, riderId, adminMode]);

  useEffect(() => {
    subscribe();

    return () => {
      const supabase = createClient();
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      if (riderChannelRef.current) {
        supabase.removeChannel(riderChannelRef.current);
        riderChannelRef.current = null;
      }
    };
  }, [subscribe]);
}
